import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { endSession, onSessionExpired, refreshAccessToken, startSession } from '../api/client';
import { toAppError } from '../api/errors';
import * as repo from '../data/repository';
import type { Session } from '../domain/models';

// Who is signed in. The router shows sign-in to signed-out people and nothing else.

export type AuthStatus = 'unknown' | 'offline' | 'signedOut' | 'signedIn';

interface Auth {
  status: AuthStatus;
  session: Session | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** Tries the saved session again (after `offline`). */
  restore: () => Promise<void>;
}

const AuthContext = createContext<Auth | null>(null);

/** The previous session, if its cookie still works. */
async function savedSession(): Promise<Session | 'signedOut' | 'offline'> {
  const outcome = await refreshAccessToken();
  if (outcome !== 'ok') return outcome === 'rejected' ? 'signedOut' : 'offline';
  try {
    return await repo.me();
  } catch (e) {
    return toAppError(e).kind === 'network' ? 'offline' : 'signedOut';
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [status, setStatus] = useState<AuthStatus>('unknown');
  const [session, setSession] = useState<Session | null>(null);

  const signedOut = useCallback(() => {
    setSession(null);
    setStatus('signedOut');
    qc.clear();
  }, [qc]);

  /** Applies what the server said about the saved session. */
  const settle = useCallback(
    (found: Session | 'signedOut' | 'offline') => {
      if (found === 'signedOut') return signedOut();
      if (found === 'offline') return setStatus('offline');
      setSession(found);
      setStatus('signedIn');
    },
    [signedOut],
  );

  const restore = useCallback(async () => {
    setStatus('unknown');
    settle(await savedSession());
  }, [settle]);

  useEffect(() => {
    void savedSession().then(settle);
    return onSessionExpired(signedOut);
  }, [settle, signedOut]);

  const signIn = useCallback(async (email: string, password: string) => {
    await startSession(email, password);
    setSession(await repo.me());
    setStatus('signedIn');
  }, []);

  const signOut = useCallback(async () => {
    await endSession();
    signedOut();
  }, [signedOut]);

  const value = useMemo(() => ({ status, session, signIn, signOut, restore }), [status, session, signIn, signOut, restore]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): Auth {
  const a = useContext(AuthContext);
  if (!a) throw new Error('useAuth outside AuthProvider');
  return a;
}
